import React from "react";
import { Box, Typography } from "@mui/material";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../theme/colors";

const REMOVE_BADGE_RED = "#dc2626";

const ATTACHMENTS = [
  { id: "poll", shortLabel: "Poll", label: "Poll", icon: PollOutlinedIcon },
  { id: "location", shortLabel: "Place", label: "Location", icon: LocationOnOutlinedIcon },
  { id: "actionType", shortLabel: "Step", label: "What I did and encourage others to share their tasks", icon: EditNoteOutlinedIcon },
  { id: "bill", shortLabel: "Bill", label: "Link to a bill", icon: GavelOutlinedIcon },
  { id: "event", shortLabel: "Event", label: "Link to an event", icon: CalendarTodayOutlinedIcon },
  { id: "image", shortLabel: "Photo", label: "Image", icon: PhotoCameraOutlinedIcon },
  { id: "externalLink", shortLabel: "Link", label: "External link", icon: LinkOutlinedIcon },
];

export default function ComposerAttachmentBar({
  active,
  onToggle,
  disabled = false,
  lockedIds = [],
}) {
  const lockedSet = new Set(lockedIds);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.text, mb: 1 }}>
        Add to your post
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))",
          gap: 1,
          width: "100%",
          pt: 0.5,
        }}
      >
        {ATTACHMENTS.map(({ id, shortLabel, label, icon: Icon }) => {
          const isActive = !!active[id];
          const isLocked = lockedSet.has(id);
          const isDisabled = disabled || isLocked;
          const showRemoveBadge = isActive && !isLocked && !disabled;

          return (
            <Box
              key={id}
              component="button"
              type="button"
              aria-label={
                isLocked
                  ? `${label} cannot be changed after votes`
                  : isActive
                    ? `Remove ${label}`
                    : `Add ${label}`
              }
              aria-pressed={isActive}
              disabled={isDisabled}
              onClick={() => !isDisabled && onToggle(id, !isActive)}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                width: "100%",
                minHeight: 44,
                px: 1,
                py: 0.75,
                borderRadius: "10px",
                border: `1px solid ${theme.primary}`,
                bgcolor: isActive ? theme.primary : theme.background,
                color: isActive ? theme.background : theme.primary,
                cursor: isDisabled ? "default" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                "&:hover": isDisabled
                  ? {}
                  : {
                      bgcolor: isActive ? theme.primaryHover : theme.background,
                      borderColor: theme.primaryHover,
                      color: isActive ? theme.background : theme.primaryHover,
                    },
                "&:focus-visible": {
                  boxShadow: theme.joinFocusRing,
                  outline: "none",
                },
              }}
            >
              {showRemoveBadge && (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: theme.background,
                    border: `1.5px solid ${REMOVE_BADGE_RED}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.2)",
                    pointerEvents: "none",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13, color: REMOVE_BADGE_RED, fontWeight: 700 }} />
                </Box>
              )}
              <Icon sx={{ fontSize: 18, color: "inherit", flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 600,
                  lineHeight: 1.2,
                  color: "inherit",
                  textAlign: "center",
                }}
              >
                {shortLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
